/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect } from 'react';
import { Company, PaymentHistory, ActivityLog, User } from '../types';
import { formatColombiaDateTime } from '../utils/colombiaTime';
import { 
  getSavedCompanies, 
  saveCompanies, 
  getSavedPayments, 
  savePayments, 
  getSavedLogs, 
  saveLogs, 
  addAuditLog 
} from '../utils/saasUtils';
import { uploadItemToFirestore, deleteItemFromFirestore, syncRegistry } from '../utils/firebaseSync';
import { 
  Building2, 
  Settings, 
  Unlock, 
  AlertTriangle, 
  Calendar, 
  Search, 
  FileSpreadsheet, 
  TrendingUp, 
  Users, 
  DollarSign, 
  Clock, 
  Activity, 
  Edit3, 
  ShieldAlert, 
  Plus, 
  Check, 
  X,
  PlusCircle,
  HelpCircle,
  Trash2
} from 'lucide-react';

interface SaaSAdminPanelProps {
  currentUser: User;
  users: User[];
  onUpdateUserStatus: (userId: string, newStatus: 'ACTIVO' | 'INACTIVO') => void;
}

export default function SaaSAdminPanel({ currentUser, users, onUpdateUserStatus }: SaaSAdminPanelProps) {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [payments, setPayments] = useState<PaymentHistory[]>([]);
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  
  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSubTab, setActiveSubTab] = useState<'empresas' | 'pagos' | 'logs' | 'users_permisos'>('empresas');

  // Config State
  const [defaultTrialDays, setDefaultTrialDays] = useState<number>(() => {
    const saved = localStorage.getItem('insumedic_trial_duration_config');
    return saved ? parseInt(saved) : 15;
  });

  // Action states
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [newStatus, setNewStatus] = useState<'Activa' | 'Suspendida' | 'Vencida' | 'En prueba gratuita'>('En prueba gratuita');
  const [newApproval, setNewApproval] = useState<'APROBADO' | 'PENDIENTE' | 'BLOQUEADO'>('APROBADO');
  const [extendDays, setExtendDays] = useState<string>('30');
  const [isVitalicia, setIsVitalicia] = useState<boolean>(true);
  const [editingFechaInicio, setEditingFechaInicio] = useState<string>('');
  const [editingFechaVencimiento, setEditingFechaVencimiento] = useState<string>('');
  
  // Create user inside tenant state
  const [searchUserTerm, setSearchUserTerm] = useState('');

  // Delete company confirmation state
  const [companyToDelete, setCompanyToDelete] = useState<Company | null>(null);

  // Notifications
  const [infoAlert, setInfoAlert] = useState('');

  // Load databases
  useEffect(() => {
    setCompanies(getSavedCompanies());
    setPayments(getSavedPayments());
    setLogs(getSavedLogs());

    const unsubLogs = syncRegistry.subscribe<ActivityLog>('logs', (data) => {
      setLogs(data);
    });
    return () => {
      unsubLogs();
    };
  }, []);

  // Save changes help
  const handleSaveTrialConfig = (e: React.FormEvent) => {
    e.preventDefault();
    localStorage.setItem('insumedic_trial_duration_config', defaultTrialDays.toString());
    setInfoAlert(`¡Configuración de prueba gratuita ajustada a ${defaultTrialDays} días de forma exitosa!`);
    addAuditLog(
      currentUser.nombreCompleto,
      'SaaS Config',
      'Configuración Prueba',
      `Duración inicial de la prueba gratuita modificada a ${defaultTrialDays} días.`,
      'system',
      'Administración Global SaaS'
    );
  };

  // Manual approval & date configuration modification
  const handleUpdateCompanySettings = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingCompany) return;

    let finalVencimiento = editingCompany.fechaVencimiento || '2099-12-31T23:59:59Z';
    let finalRegistro = editingCompany.fechaRegistro || new Date().toISOString();

    if (isVitalicia) {
      finalVencimiento = '2099-12-31T23:59:59Z';
    } else {
      if (editingFechaVencimiento) {
        finalVencimiento = new Date(`${editingFechaVencimiento}T23:59:59Z`).toISOString();
      }
    }

    if (editingFechaInicio) {
      finalRegistro = new Date(`${editingFechaInicio}T08:00:00Z`).toISOString();
    }

    const companiesDb = getSavedCompanies();
    const updated = companiesDb.map(c => {
      if (c.id === editingCompany.id) {
        const uComp: Company = {
          ...c,
          estadoSuscripcion: newStatus,
          estadoAprobacion: newApproval,
          fechaRegistro: finalRegistro,
          fechaVencimiento: finalVencimiento,
          esAcreditacionVitalicia: isVitalicia
        };
        uploadItemToFirestore<Company>('companies', uComp);
        return uComp;
      }
      return c;
    });

    saveCompanies(updated);
    setCompanies(updated);
    setEditingCompany(null);
    setInfoAlert(`¡La configuración de vigencia y estado para "${editingCompany.nombreEmpresa}" ha sido actualizada con éxito!`);
    
    addAuditLog(
      currentUser.nombreCompleto,
      'SaaS Admin',
      'Modificación Cliente',
      `Ajustó vigencia (Vitalicia: ${isVitalicia ? 'SI' : 'NO'}, Vence: ${finalVencimiento.slice(0, 10)}) y estado a: ${newStatus} para ${editingCompany.nombreEmpresa}`,
      editingCompany.id,
      editingCompany.nombreEmpresa
    );
  };

  // Manual Trial/Plan extension
  const handleExtendAccessDays = (companyId: string, days: number) => {
    const companiesDb = getSavedCompanies();
    const target = companiesDb.find(c => c.id === companyId);
    if (!target) return;

    const currentExp = new Date(target.fechaVencimiento);
    const baseDate = currentExp.getTime() > Date.now() ? currentExp : new Date();
    const newExp = new Date(baseDate.getTime() + days * 24 * 60 * 60 * 1000);

    const updated = companiesDb.map(c => {
      if (c.id === companyId) {
        const uComp = {
          ...c,
          fechaVencimiento: newExp.toISOString(),
          estadoSuscripcion: 'Activa' as const // Activates upon manual extension
        };
        uploadItemToFirestore<Company>('companies', uComp);
        return uComp;
      }
      return c;
    });

    saveCompanies(updated);
    setCompanies(updated);
    setInfoAlert(`Se han extendido ${days} días de acceso para la empresa "${target.nombreEmpresa}" de manera manual.`);
    
    addAuditLog(
      currentUser.nombreCompleto,
      'SaaS Admin',
      'Extensión Manual',
      `Extendió acceso por ${days} días. Nueva fecha exp: ${newExp.toLocaleDateString()}`,
      target.id,
      target.nombreEmpresa
    );
  };

  // Approves/blocks client directly
  const handleToggleAprobacionDirect = (companyId: string, current: 'APROBADO' | 'PENDIENTE' | 'BLOQUEADO') => {
    const next: 'APROBADO' | 'BLOQUEADO' = current === 'APROBADO' ? 'BLOQUEADO' : 'APROBADO';
    const companiesDb = getSavedCompanies();
    const target = companiesDb.find(c => c.id === companyId);
    if (!target) return;

    const updated = companiesDb.map(c => {
      if (c.id === companyId) {
        const uComp = { ...c, estadoAprobacion: next };
        uploadItemToFirestore<Company>('companies', uComp);
        return uComp;
      }
      return c;
    });

    saveCompanies(updated);
    setCompanies(updated);
    setInfoAlert(`El estado de aprobación de la empresa "${target.nombreEmpresa}" se cambió a: ${next}.`);

    addAuditLog(
      currentUser.nombreCompleto,
      'SaaS Admin',
      'Estado Aprobación',
      `Cambió aprobación directa a: ${next}`,
      target.id,
      target.nombreEmpresa
    );
  };

  const executeDeleteCompany = () => {
    if (!companyToDelete) return;

    const updated = companies.filter(c => c.id !== companyToDelete.id);
    saveCompanies(updated);
    setCompanies(updated);
    deleteItemFromFirestore('companies', companyToDelete.id);
    setInfoAlert(`La empresa "${companyToDelete.nombreEmpresa}" ha sido eliminada permanentemente del sistema.`);

    addAuditLog(
      currentUser.nombreCompleto,
      'SaaS Admin',
      'Eliminación de Empresa',
      `Eliminó permanentemente a la empresa: ${companyToDelete.nombreEmpresa} (NIT: ${companyToDelete.nit})`,
      companyToDelete.id,
      companyToDelete.nombreEmpresa
    );

    setCompanyToDelete(null);
  };

  // Filter companies list
  const filteredCompanies = companies.filter(c => 
    c.nombreEmpresa.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.nit.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.ciudad.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6" id="saas-system-admin-panel">
      
      {/* Header section with Stats widgets */}
      <div className="bg-[#071A36] rounded-3xl p-6 text-white border border-[#1C4F8A]/50 relative overflow-hidden shadow-md">
        <div className="absolute top-0 right-0 w-64 h-64 bg-teal-500/10 rounded-full blur-3xl pointer-events-none"></div>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 relative z-10">
          <div className="space-y-1">
            <h2 className="text-lg font-sans font-black flex items-center gap-2 text-white">
              <span className="p-1.5 bg-[#1C4F8A] text-teal-300 rounded-lg">⚙️</span>
              CONSOLA DEL ADMINISTRADOR DEL SISTEMA
            </h2>
            <p className="text-xs text-slate-200 font-sans">
              Módulo SaaS Global para la administración de clientes empresariales, parámetros de vigencia, auditorías de accesos e historiales de recaudo.
            </p>
          </div>
          
          <div className="flex bg-[#0D274F] p-1.5 rounded-xl border border-[#1C4F8A]/60 w-full sm:w-auto">
            <button
              onClick={() => setActiveSubTab('empresas')}
              className={`flex-1 sm:flex-initial px-4 py-2 text-xs font-bold rounded-lg font-sans transition-all cursor-pointer ${
                activeSubTab === 'empresas' ? 'bg-[#1C4F8A] text-white shadow-xs' : 'text-slate-300 hover:text-white'
              }`}
            >
              Empresas
            </button>
            <button
              onClick={() => setActiveSubTab('pagos')}
              className={`flex-1 sm:flex-initial px-4 py-2 text-xs font-bold rounded-lg font-sans transition-all cursor-pointer ${
                activeSubTab === 'pagos' ? 'bg-[#1C4F8A] text-white shadow-xs' : 'text-slate-300 hover:text-white'
              }`}
            >
              Pagos Recibidos
            </button>
            <button
              onClick={() => setActiveSubTab('logs')}
              className={`flex-1 sm:flex-initial px-4 py-2 text-xs font-bold rounded-lg font-sans transition-all cursor-pointer ${
                activeSubTab === 'logs' ? 'bg-[#1C4F8A] text-white shadow-xs' : 'text-slate-300 hover:text-white'
              }`}
            >
              Registro Logs
            </button>
            <button
              onClick={() => setActiveSubTab('users_permisos')}
              className={`flex-1 sm:flex-initial px-4 py-2 text-xs font-bold rounded-lg font-sans transition-all cursor-pointer ${
                activeSubTab === 'users_permisos' ? 'bg-[#1C4F8A] text-white shadow-xs' : 'text-slate-300 hover:text-white'
              }`}
            >
              Colaboradores
            </button>
          </div>
        </div>
      </div>

      {infoAlert && (
        <div className="bg-emerald-50 border border-emerald-250 text-emerald-800 text-xs p-4 rounded-2xl flex justify-between items-center animate-fade-in shadow-xs" id="admin-saas-alert">
          <p className="font-sans font-medium">{infoAlert}</p>
          <button 
            onClick={() => setInfoAlert('')}
            className="text-emerald-700 hover:text-emerald-900 font-bold ml-4 cursor-pointer text-sm"
          >
            ✕
          </button>
        </div>
      )}

      {/* Global SaaS Configurations form - only in Empresas tab */}
      {activeSubTab === 'empresas' && (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 select-text">
          
          {/* Config column */}
          <div className="lg:col-span-1 bg-white rounded-2xl border border-slate-200 p-5 space-y-4 shadow-xs">
            <h3 className="font-sans font-black text-xs text-slate-800 uppercase tracking-wider pb-3 border-b border-slate-100 flex items-center gap-1.5">
              <Settings className="h-4 w-4 text-indigo-600" />
              Parámetros de Prueba
            </h3>

            <form onSubmit={handleSaveTrialConfig} className="space-y-3.5">
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 leading-tight">
                  Duración de Prueba (Días)
                </label>
                <input
                  type="number"
                  min={1}
                  max={90}
                  required
                  value={defaultTrialDays}
                  onChange={(e) => setDefaultTrialDays(parseInt(e.target.value) || 15)}
                  className="w-full text-xs h-9.5 border border-slate-200 rounded-lg px-2.5 font-mono bg-slate-50 focus:ring-1 focus:ring-indigo-500 focus:outline-hidden"
                />
              </div>

              <button
                type="submit"
                className="w-full h-9 bg-slate-900 hover:bg-slate-800 text-white text-[11px] font-sans font-bold uppercase tracking-wider rounded-lg transition-all cursor-pointer shadow-xs"
              >
                Ajustar Duración
              </button>
            </form>

            <div className="bg-slate-50 p-4 border border-slate-200 rounded-xl space-y-2 text-xs text-slate-600">
              <span className="font-sans font-bold text-slate-800 block">Licenciamiento SaaS</span>
              <p className="font-sans leading-relaxed text-[11px]">
                Cualquier nuevo registro corporativo tomará la vigencia configurada aquí. Se otorga de forma predeterminada acceso COMPLETO temporalizado.
              </p>
            </div>
          </div>

          {/* Central Workspace table Column */}
          <div className="lg:col-span-3 bg-white rounded-2xl border border-slate-200 shadow-xs p-5 overflow-hidden">
            <div className="pb-3 border-b border-slate-150/80 flex flex-col sm:flex-row justify-between sm:items-center gap-3">
              <h3 className="font-sans font-black text-xs text-slate-900 uppercase tracking-wider">
                Control de Empresas y Clientes
              </h3>
              
              <div className="relative max-w-xs w-full">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar por Empresa, NIT, Ciudad..."
                  className="w-full h-9 text-xs pl-8.5 pr-3 border border-slate-200 rounded-lg bg-slate-50 text-slate-900"
                />
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs mt-3">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-100/50 text-slate-500 font-bold uppercase text-[9px] tracking-wider">
                    <th className="p-3">Empresa</th>
                    <th className="p-3">NIT</th>
                    <th className="p-3">Plan / Suscripción</th>
                    <th className="p-3">Acreditación</th>
                    <th className="p-3 text-center">Aprobación</th>
                    <th className="p-3 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredCompanies.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="p-8 text-center text-slate-400 font-sans">
                        No se ha ubicado ninguna empresa que coincida con la búsqueda.
                      </td>
                    </tr>
                  ) : (
                    filteredCompanies.map(c => {
                      const expDays = Math.max(0, Math.ceil((new Date(c.fechaVencimiento).getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
                      
                      return (
                        <tr key={c.id} className="hover:bg-slate-50/50 transition-colors">
                          <td className="p-3">
                            <div className="font-bold text-slate-800 text-[12px]">{c.nombreEmpresa}</div>
                            <span className="block text-[10px] text-slate-400">{c.ciudad}, {c.pais} • {c.correoContacto}</span>
                          </td>
                          <td className="p-3 font-mono font-medium text-slate-650">{c.nit}</td>
                          <td className="p-3">
                            <span className={`inline-block p-1 px-2 text-[10px] font-bold rounded-full ${
                              c.estadoSuscripcion === 'Activa' ? 'bg-emerald-50 text-emerald-700' :
                              c.estadoSuscripcion === 'En prueba gratuita' ? 'bg-amber-50 text-amber-700' :
                              'bg-rose-50 text-rose-700'
                            }`}>
                              {c.estadoSuscripcion}
                            </span>
                          </td>
                          <td className="p-3">
                            {c.esAcreditacionVitalicia === true || (c.fechaVencimiento && new Date(c.fechaVencimiento).getFullYear() >= 2099) ? (
                              <>
                                <span className="block font-sans font-extrabold text-teal-700 bg-teal-50 px-2 py-0.5 rounded-md inline-block text-[10px] uppercase tracking-wider">Acreditación Vitalicia</span>
                                <span className="block text-[10px] text-slate-500 mt-0.5">Acceso Ilimitado</span>
                              </>
                            ) : (
                              <>
                                <div className="flex items-center gap-1.5">
                                  <span className={`block font-bold font-mono text-[11px] ${expDays <= 5 ? 'text-amber-600 font-extrabold' : 'text-slate-700'}`}>
                                    {expDays} Días Restantes
                                  </span>
                                  {expDays <= 5 && (
                                    <span className="text-[9px] bg-amber-100 text-amber-800 font-black px-1.5 py-0.2 rounded-full border border-amber-300 animate-pulse">
                                      ⚠️ 5 Días
                                    </span>
                                  )}
                                </div>
                                <span className="block text-[10px] text-slate-400 font-mono mt-0.5">
                                  Vence: {new Date(c.fechaVencimiento).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                </span>
                                {c.fechaRegistro && (
                                  <span className="block text-[9px] text-slate-400 font-mono">
                                    Inicio: {new Date(c.fechaRegistro).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                  </span>
                                )}
                              </>
                            )}
                          </td>
                          <td className="p-3 text-center">
                            <button
                              onClick={() => handleToggleAprobacionDirect(c.id, c.estadoAprobacion)}
                              className={`p-1 px-2.5 font-bold font-sans text-[10px] rounded-lg cursor-pointer transition-colors ${
                                c.estadoAprobacion === 'APROBADO' 
                                  ? 'bg-emerald-50 text-emerald-700 hover:bg-rose-50 hover:text-rose-700' 
                                  : 'bg-rose-100 text-rose-700 hover:bg-emerald-50 hover:text-emerald-700'
                              }`}
                              title="Click rápido para alternar aprobación o bloqueo de cliente"
                            >
                              {c.estadoAprobacion === 'APROBADO' ? '✅ Aprovado / Activo' : '🚫 BLOQUEADO'}
                            </button>
                          </td>
                          <td className="p-3 text-right space-x-1">
                            <button
                              onClick={() => {
                                setEditingCompany(c);
                                setNewStatus(c.estadoSuscripcion);
                                setNewApproval(c.estadoAprobacion);

                                const isVit = c.esAcreditacionVitalicia === true || (c.fechaVencimiento ? new Date(c.fechaVencimiento).getFullYear() >= 2099 : false);
                                setIsVitalicia(isVit);

                                const regDate = c.fechaRegistro ? new Date(c.fechaRegistro) : new Date();
                                setEditingFechaInicio(isNaN(regDate.getTime()) ? new Date().toISOString().slice(0, 10) : regDate.toISOString().slice(0, 10));

                                const expDate = c.fechaVencimiento ? new Date(c.fechaVencimiento) : new Date(Date.now() + 30 * 86400000);
                                const validExp = isNaN(expDate.getTime()) ? new Date(Date.now() + 30 * 86400000) : expDate;
                                setEditingFechaVencimiento(validExp.toISOString().slice(0, 10));
                              }}
                              className="p-1 px-2.5 bg-slate-100 hover:bg-slate-200 text-slate-705 font-bold font-sans text-[10px] rounded-lg cursor-pointer transition-colors inline-flex items-center gap-1"
                              id={`edit-company-btn-${c.id}`}
                            >
                              <Edit3 className="h-3 w-3" />
                              <span>Editar</span>
                            </button>
                            <button
                              onClick={() => handleExtendAccessDays(c.id, 30)}
                              className="p-1 px-2.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-bold font-sans text-[10px] rounded-lg cursor-pointer transition-colors inline-flex items-center gap-1"
                              title="Extender acceso por 30 días adicionales de forma rápida"
                            >
                              <Plus className="h-3 w-3" />
                              <span>+30 Días</span>
                            </button>
                            <button
                              onClick={() => {
                                if (c.id === 'company-insumedic') {
                                  setInfoAlert('Por seguridad del sistema, no se puede eliminar la empresa principal de operación (Quality Procesos y Servicios S.A.S.).');
                                  return;
                                }
                                if (currentUser.companyId === c.id) {
                                  setInfoAlert('No puede eliminar la empresa asociada a su sesión activa actual.');
                                  return;
                                }
                                setCompanyToDelete(c);
                              }}
                              className="p-1 px-2.5 bg-rose-50 hover:bg-rose-100 text-rose-700 font-bold font-sans text-[10px] rounded-lg cursor-pointer transition-colors inline-flex items-center gap-1"
                              title="Eliminar esta empresa permanentemente del sistema"
                              id={`delete-btn-${c.id}`}
                            >
                              <Trash2 className="h-3 w-3" />
                              <span>Eliminar</span>
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

          </div>

        </div>
      )}

      {/* Global SaaS Payments history panel */}
      {activeSubTab === 'pagos' && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-xs p-5 select-text">
          <div className="pb-3 border-b border-slate-150 flex items-center justify-between">
            <h3 className="font-sans font-black text-xs text-slate-900 uppercase tracking-wider">
              Historial de Recaudos y Transacciones Globales (SaaS)
            </h3>
            <DollarSign className="h-4 w-4 text-emerald-600" />
          </div>

          <div className="overflow-x-auto text-xs mt-3">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-slate-250 bg-slate-50 text-slate-500 font-bold uppercase text-[9px]">
                  <th className="p-3">Comprobante</th>
                  <th className="p-3">Empresa Cliente</th>
                  <th className="p-3">Fecha</th>
                  <th className="p-3">Pasarela Recibo</th>
                  <th className="p-3">ID Transacción</th>
                  <th className="p-3 text-right">Monto</th>
                  <th className="p-3 text-center">Estado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-150">
                {payments.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="p-8 text-center text-slate-400 font-sans">
                      Ningún cobro ha sido procesado hasta el momento en la plataforma SaaS.
                    </td>
                  </tr>
                ) : (
                  payments.map(pay => (
                    <tr key={pay.id} className="hover:bg-slate-50/5 transition-colors">
                      <td className="p-3 font-mono font-bold text-slate-800">{pay.comprobanteNumero}</td>
                      <td className="p-3 font-bold text-slate-750">{pay.nombreEmpresa}</td>
                      <td className="p-3 text-slate-500">{new Date(pay.fecha).toLocaleString()}</td>
                      <td className="p-3 font-sans text-slate-600">{pay.metodoPago}</td>
                      <td className="p-3 font-mono text-slate-450">{pay.idTransaccion}</td>
                      <td className="p-3 font-sans font-extrabold text-slate-900 text-right">${pay.monto.toLocaleString()} COP</td>
                      <td className="p-3 text-center">
                        <span className="p-1 px-2.5 text-[9px] font-bold bg-emerald-50 text-emerald-700 rounded-full font-sans">
                          {pay.estado}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Global Audit Logs panel */}
      {activeSubTab === 'logs' && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-xs p-5 select-text">
          <div className="pb-3 border-b border-slate-150 flex items-center justify-between">
            <h3 className="font-sans font-black text-xs text-slate-900 uppercase tracking-wider flex items-center gap-1.5 font-mono">
              <Activity className="h-4 w-4 text-slate-400 animate-pulse" />
              Auditoría Técnica y Registro de Logs de Trazabilidad FDA
            </h3>
            <span className="text-[10px] font-mono text-slate-400">Total logs: {logs.length}</span>
          </div>

          <div className="overflow-x-auto text-[11px] mt-3 font-mono">
            <table className="w-full text-left border-collapse text-left">
              <thead>
                <tr className="border-b border-slate-250 bg-slate-50 text-slate-500 font-bold uppercase text-[9px] tracking-wider">
                  <th className="p-2.5">Fecha y Hora</th>
                  <th className="p-2.5">Empresa / Tenant</th>
                  <th className="p-2.5">Operador</th>
                  <th className="p-2.5">Módulo</th>
                  <th className="p-2.5">Acción</th>
                  <th className="p-2.5">Mensaje Detallado del Log</th>
                  <th className="p-2.5 text-right">Dirección IP</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-150 text-slate-700">
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="p-8 text-center text-slate-400 font-sans">
                      Historial de de auditoría FDA vacío.
                    </td>
                  </tr>
                ) : (
                  logs.map(lg => (
                    <tr key={lg.id} className="hover:bg-slate-50 transition-colors">
                      <td className="p-2.5 text-slate-700 whitespace-nowrap font-mono font-semibold">{formatColombiaDateTime(lg.timestamp, true)}</td>
                      <td className="p-2.5 text-slate-900 font-sans font-semibold text-xs leading-none max-w-[140px] truncate" title={lg.nombreEmpresa}>{lg.nombreEmpresa || 'Quality Procesos y Servicios S.A.S.'}</td>
                      <td className="p-2.5 font-sans font-bold text-slate-800 text-xs shrink-0">{lg.usuario}</td>
                      <td className="p-2.5 text-indigo-650 font-bold text-[10px] uppercase leading-none">{lg.modulo}</td>
                      <td className="p-2.5"><span className="p-1 px-1.5 bg-slate-100 text-slate-700 font-bold rounded-md leading-none">{lg.accion}</span></td>
                      <td className="p-2.5 font-sans text-xs text-slate-600 max-w-sm truncate" title={lg.detalle}>{lg.detalle}</td>
                      <td className="p-2.5 text-slate-400 text-right">{lg.ipAddress}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Embedded direct user administrator for multitenancy */}
      {activeSubTab === 'users_permisos' && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-xs p-5 select-text">
          <div className="pb-3 border-b border-slate-150 flex flex-col sm:flex-row justify-between sm:items-center gap-3">
            <h3 className="font-sans font-black text-xs text-slate-900 uppercase tracking-wider">
              Administración de Colaboradores e Insumos de Planta (Global)
            </h3>
            <div className="relative max-w-xs w-full">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
              <input
                type="text"
                value={searchUserTerm}
                onChange={(e) => setSearchUserTerm(e.target.value)}
                placeholder="Filtrar colaboradores por nombre..."
                className="w-full h-9 text-xs pl-8.5 pr-3 border border-slate-200 rounded-lg bg-slate-50"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-xs mt-3">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-100/50 text-slate-400 font-bold uppercase text-[9px]">
                  <th className="p-3">Nombre Completo</th>
                  <th className="p-3">Cédula / Password</th>
                  <th className="p-3">Cargo Operacional</th>
                  <th className="p-3">Usuario Acceso</th>
                  <th className="p-3">Lote Tenant</th>
                  <th className="p-3 text-center">Estado de Acceso</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.filter(u => u.nombreCompleto.toLowerCase().includes(searchUserTerm.toLowerCase())).map(u => {
                  const companyObj = companies.find(c => c.id === (u.companyId || 'company-insumedic'));
                  return (
                    <tr key={u.id} className="hover:bg-slate-50 transition-colors">
                      <td className="p-3 font-bold text-slate-800 text-[12px]">{u.nombreCompleto}</td>
                      <td className="p-3 font-mono text-slate-400">{u.cedula}</td>
                      <td className="p-3 font-sans text-slate-700 font-medium">{u.cargo}</td>
                      <td className="p-3 font-mono font-bold text-slate-800">{u.username}</td>
                      <td className="p-3"><span className="text-[11px] font-sans font-bold text-indigo-700" title={companyObj?.nombreEmpresa}>{companyObj?.nombreEmpresa || 'Quality Procesos y Servicios S.A.S.'}</span></td>
                      <td className="p-3 text-center">
                        <button
                          onClick={() => onUpdateUserStatus(u.id, u.estado === 'ACTIVO' ? 'INACTIVO' : 'ACTIVO')}
                          className={`p-1 px-3.5 font-sans font-bold text-[10px] rounded-lg cursor-pointer ${
                            u.estado === 'ACTIVO' ? 'bg-emerald-50 text-emerald-800 hover:bg-slate-100' : 'bg-red-50 text-rose-700 hover:bg-slate-100'
                          }`}
                        >
                          {u.estado || 'ACTIVO'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* 4. MODAL FOR EDITING COMPANY ACCREDITATION / APPROVAL */}
      {/* ======================================================== */}
      {editingCompany && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-50 animate-fade-in animate-duration-150">
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-2xl max-w-md w-full">
            
            <div className="bg-slate-900 p-4.5 text-white flex justify-between items-center text-xs">
              <span className="font-sans font-black">EDITAR ACUERDO DE CLIENTE</span>
              <button 
                onClick={() => setEditingCompany(null)}
                className="text-slate-400 hover:text-white font-sans font-bold text-sm cursor-pointer"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleUpdateCompanySettings} className="p-5 space-y-4 text-xs font-sans">
              <p className="text-[11px] text-slate-500">
                Realice modificaciones a la vigencia y licencia SaaS de <strong>{editingCompany.nombreEmpresa}</strong>.
              </p>

              {/* Modalidad de Acreditación / Licencia */}
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                  Modalidad de Licencia / Acreditación
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setIsVitalicia(true)}
                    className={`py-2 px-2.5 text-[11px] font-bold rounded-xl border transition-all cursor-pointer text-center ${
                      isVitalicia
                        ? 'bg-teal-50 border-teal-500 text-teal-800 ring-2 ring-teal-200'
                        : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                    }`}
                    id="opt-vitalicia"
                  >
                    Acreditación Vitalicia
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsVitalicia(false)}
                    className={`py-2 px-2.5 text-[11px] font-bold rounded-xl border transition-all cursor-pointer text-center ${
                      !isVitalicia
                        ? 'bg-indigo-50 border-indigo-500 text-indigo-800 ring-2 ring-indigo-200'
                        : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                    }`}
                    id="opt-periodo-config"
                  >
                    Periodo Configurado
                  </button>
                </div>
              </div>

              {/* Start & Expiration dates when NOT vitalicia */}
              {!isVitalicia && (
                <div className="grid grid-cols-2 gap-3 bg-indigo-50/60 p-3.5 rounded-2xl border border-indigo-100">
                  <div>
                    <label className="block text-[10px] font-extrabold text-indigo-900 uppercase tracking-wider mb-1">
                      Fecha Inicio Plan
                    </label>
                    <input
                      type="date"
                      required
                      value={editingFechaInicio}
                      onChange={(e) => setEditingFechaInicio(e.target.value)}
                      className="w-full text-xs h-9 border border-indigo-200 rounded-lg px-2 bg-white font-mono focus:ring-1 focus:ring-indigo-500 text-slate-800 font-bold"
                      id="input-fecha-inicio-plan"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-extrabold text-indigo-900 uppercase tracking-wider mb-1">
                      Fecha Vencimiento
                    </label>
                    <input
                      type="date"
                      required
                      value={editingFechaVencimiento}
                      onChange={(e) => setEditingFechaVencimiento(e.target.value)}
                      className="w-full text-xs h-9 border border-indigo-200 rounded-lg px-2 bg-white font-mono focus:ring-1 focus:ring-indigo-500 text-slate-800 font-bold"
                      id="input-fecha-vencimiento-plan"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                  Estado de Suscripción Recurrente
                </label>
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value as any)}
                  className="w-full text-xs h-9.5 border border-slate-200 rounded-lg px-2 bg-slate-50 font-sans cursor-pointer focus:outline-hidden"
                >
                  <option value="Activa">Activa</option>
                  <option value="Suspendida">Suspendida</option>
                  <option value="Vencida">Vencida</option>
                  <option value="En prueba gratuita">En prueba gratuita</option>
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                  Estado de Aprobación de Cliente
                </label>
                <select
                  value={newApproval}
                  onChange={(e) => setNewApproval(e.target.value as any)}
                  className="w-full text-xs h-9.5 border border-slate-200 rounded-lg px-2 bg-slate-50 font-sans cursor-pointer focus:outline-hidden"
                >
                  <option value="APROBADO">APROBADO (Tiene Acceso)</option>
                  <option value="PENDIENTE">PENDIENTE de revisión</option>
                  <option value="BLOQUEADO">BLOQUEADO (Acceso Denegado)</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setEditingCompany(null)}
                  className="w-full h-9 bg-slate-100 font-sans text-slate-600 rounded-lg font-bold text-xs cursor-pointer hover:bg-slate-200 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="w-full h-9 bg-teal-600 hover:bg-teal-700 text-white font-sans rounded-lg font-black text-xs cursor-pointer transition-colors shadow-xs"
                >
                  Guardar Ajustes
                </button>
              </div>
            </form>

          </div>
        </div>
      )}

      {/* 5. MODAL FOR DELETING COMPANY PERMANENTLY */}
      {companyToDelete && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-50 animate-fade-in animate-duration-150">
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-2xl max-w-md w-full select-none">
            
            <div className="bg-rose-950 p-4.5 text-white flex justify-between items-center text-xs">
              <span className="font-sans font-black tracking-wider flex items-center gap-2">
                <AlertTriangle className="h-4.5 w-4.5 text-rose-400 animate-bounce" />
                ELIMINAR EMPRESA PERMANENTEMENTE
              </span>
              <button 
                onClick={() => setCompanyToDelete(null)}
                className="text-rose-200 hover:text-white font-sans font-bold text-sm cursor-pointer"
              >
                ✕
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div className="bg-rose-50 border border-rose-100 p-4 rounded-xl flex items-start gap-3">
                <ShieldAlert className="h-5 w-5 text-rose-600 shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <p className="text-xs font-bold text-rose-900 font-sans">¡Atención, Acción Crítica e Irreversible!</p>
                  <p className="text-[11px] text-rose-750 font-sans leading-relaxed">
                    Está a punto de borrar permanentemente la empresa <strong className="font-extrabold">{companyToDelete.nombreEmpresa}</strong> (NIT: {companyToDelete.nit}).
                  </p>
                </div>
              </div>

              <div className="space-y-2 text-xs text-slate-600 font-sans leading-relaxed">
                <p>Al confirmar esta eliminación, se realizarán las siguientes acciones:</p>
                <ul className="list-disc pl-4 space-y-1 text-[11px]">
                  <li>Se retirará la empresa de la lista habilitada de multi-inquilinos (multi-tenancy).</li>
                  <li>Ningún usuario o colaborador registrado bajo esta empresa podrá iniciar sesión.</li>
                  <li>Todas las configuraciones e historiales locales vinculados perderán su asignación directa.</li>
                </ul>
              </div>

              <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setCompanyToDelete(null)}
                  className="w-full h-9 bg-slate-100 font-sans text-slate-650 hover:bg-slate-250 rounded-lg font-bold text-xs cursor-pointer transition-colors"
                >
                  Cancelar, Conservar
                </button>
                <button
                  type="button"
                  onClick={executeDeleteCompany}
                  className="w-full h-9 bg-rose-600 hover:bg-rose-700 active:bg-rose-800 text-white font-sans rounded-lg font-black text-xs cursor-pointer transition-colors flex items-center justify-center gap-1.5 shadow-xs"
                  id="confirm-delete-company-btn"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  <span>Sí, Eliminar</span>
                </button>
              </div>
            </div>

          </div>
        </div>
      )}

    </div>
  );
}
